import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Download, AlertTriangle, Coins, CalendarRange, Info, ChevronRight,
} from 'lucide-react';
import {
  DataTable, Person, Button, PageHeader, Modal, FormField, StatusPill,
} from '../../components';
import { payrollService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { isOwnerRole } from '../../config/menuCodeMap';

/**
 * Staff > Payroll — what people are paid, and what they are owed.
 *
 * Two tabs, because they are two different jobs done at different times:
 *   • PAY RUN      — pick a period, see hours and pay per person, export a CSV.
 *   • HOURLY RATES — set what each person earns.
 *
 * NOTHING ON THIS PAGE CALCULATES MONEY. Pay arrives already worked out — hours
 * x the person's hourly rate, done once on the server so this page and anything
 * else that reports pay can never disagree. See api/utils/payrollService.js.
 *
 * The rate lives in ONE place (a staff member's hourly rate) and is read live, so
 * changing it also changes what an earlier month exports as. The downloaded CSV is
 * therefore the record of what was actually paid.
 *
 * The two things this page surfaces rather than hiding behind a confident total:
 *   • NEEDS A RATE   — worked hours with nobody's rate to pay them at.
 *   • NOT INCLUDED   — work that isn't payable yet (unapproved hours, a visit
 *                      with no clock-out), listed with the next action.
 */

const MANAGER_ROLE_IDS = [2, 4];

const CARD = 'bg-white dark:bg-[#262b33] border border-[#E2E8F0] dark:border-gray-800 rounded-2xl shadow-sm';

const TABS = [
  { key: 'run', label: 'Pay run' },
  { key: 'rates', label: 'Hourly rates' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SHORT_MONTHS = MONTH_NAMES.map((m) => m.slice(0, 3));

/**
 * How the pay period is chosen. Care organisations run pay on different cycles
 * — calendar month, a four-week cycle, or weekly — and a one-off query (a
 * leaver's final pay, a query about a fortnight) needs two dates and nothing
 * else. All four end up as the same from/to the API takes.
 */
const PERIOD_TYPES = [
  { key: 'month', label: 'Monthly' },
  { key: 'four', label: '4-weekly' },
  { key: 'week', label: 'Weekly' },
  { key: 'custom', label: 'Custom dates' },
];

/** How many past periods each cycle offers to look back over. */
const LOOKBACK = { month: 12, four: 8, week: 12 };

/**
 * Local YYYY-MM-DD. Never toISOString() — that converts to UTC, which turns
 * an evening in London into the previous day and would silently shift a pay
 * period by one day.
 */
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d, n) => {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  c.setDate(c.getDate() + n);
  return c;
};

/** Monday of the week a date falls in — pay weeks run Monday to Sunday. */
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));

const parseYmd = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const isYmd = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));

/** "4 Aug – 31 Aug 2026", dropping the repeated year and month where it reads. */
const rangeLabel = (from, to) => {
  const a = parseYmd(from);
  const b = parseYmd(to);
  const left = a.getFullYear() === b.getFullYear()
    ? `${a.getDate()} ${SHORT_MONTHS[a.getMonth()]}`
    : `${a.getDate()} ${SHORT_MONTHS[a.getMonth()]} ${a.getFullYear()}`;
  return `${left} – ${b.getDate()} ${SHORT_MONTHS[b.getMonth()]} ${b.getFullYear()}`;
};

/** The current month and the eleven before it — a year of lookback. */
const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < LOOKBACK.month; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
};

/**
 * Whole weeks and whole four-week cycles, counted back from the week we are in
 * now. Both are anchored to Monday, so a week is never half of one cycle and
 * half of the next, and the cycles tile the calendar without a gap or overlap.
 *
 * The value of an option is the start date; weekSpan is 1 for weekly and 4
 * for the four-week cycle.
 */
const weekBasedOptions = (weekSpan) => {
  const out = [];
  const thisMonday = mondayOf(new Date());
  const latestStart = addDays(thisMonday, -7 * (weekSpan - 1));
  const count = weekSpan === 1 ? LOOKBACK.week : LOOKBACK.four;

  for (let i = 0; i < count; i += 1) {
    const start = addDays(latestStart, -7 * weekSpan * i);
    const end = addDays(start, 7 * weekSpan - 1);
    out.push({
      value: ymd(start),
      label: `${rangeLabel(ymd(start), ymd(end))}${i === 0 ? ' (current)' : ''}`,
    });
  }
  return out;
};

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Start of the week/cycle we are in now, as the option values express it. */
const currentWeekStart = (weekSpan) => ymd(addDays(mondayOf(new Date()), -7 * (weekSpan - 1)));

/** Trim the pointless decimals on hours: 8.00 → "8", 7.50 → "7.5". */
const hoursLabel = (n) => {
  const v = Number(n || 0);
  return v === Math.round(v) ? String(Math.round(v)) : String(Number(v.toFixed(2)));
};

/**
 * Money, always to two decimals. No currency symbol: the organisation's currency
 * isn't recorded anywhere, and inventing a "£" for an org that doesn't use it is
 * worse than a bare figure a payroll clerk can read either way.
 */
const money = (n) => (n == null ? '—' : Number(n).toFixed(2));

/** Pay codes for the leave rows in the export, mirroring 'REG' for worked time. */
const LEAVE_CODES = { annual: 'ANL', sick: 'SICK', other: 'OTH' };

/**
 * Leave is reported by TYPE and never summed into one "days off" figure:
 * annual leave comes out of an allowance, sickness is a different policy, and
 * a payroll clerk has to treat them differently. Same three buckets the API
 * splits on — see api/utils/payrollService.js.
 */
const LEAVE_KINDS = [
  { key: 'annual', label: 'Annual leave', short: 'Annual' },
  { key: 'sick', label: 'Sickness', short: 'Sick' },
  { key: 'other', label: 'Other leave', short: 'Other' },
];

/** "2 days" / "1 day" / "1.5 days", or nothing at all when there is none. */
const daysLabel = (n) => {
  const v = Number(n || 0);
  if (!v) return null;
  return `${hoursLabel(v)} day${v === 1 ? '' : 's'}`;
};

const Payroll = () => {
  const { user } = useAuth();
  const allowed = MANAGER_ROLE_IDS.includes(Number(user?.role?.id)) || isOwnerRole(user);

  const [tab, setTab] = useState('run');

  // Pay run. The cycle is picked first; each cycle keeps its own selection, so
  // flipping between them to compare doesn't lose where you were.
  const [periodType, setPeriodType] = useState('month');
  const [month, setMonth] = useState(thisMonth);
  const [weekStart, setWeekStart] = useState(() => currentWeekStart(1));
  const [cycleStart, setCycleStart] = useState(() => currentWeekStart(4));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [run, setRun] = useState(null);
  const [runLoading, setRunLoading] = useState(true);
  // The employee whose individual lines are open, from clicking their row.
  const [detail, setDetail] = useState(null);

  // Rates
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [editRate, setEditRate] = useState(null);
  const [savingRate, setSavingRate] = useState(false);

  // Bulk rate import. The chosen file is previewed against real names before
  // anything is written — a rates file is not something to apply on trust.
  const rateFileRef = useRef(null);
  const [ratePreview, setRatePreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const months = useMemo(monthOptions, []);
  const weeks = useMemo(() => weekBasedOptions(1), []);
  const cycles = useMemo(() => weekBasedOptions(4), []);

  /**
   * The chosen cycle as the from/to the API works in. Null means the custom
   * dates aren't usable yet — nothing is fetched and the reason is shown,
   * rather than silently reporting some other period's figures.
   */
  const range = useMemo(() => {
    if (periodType === 'week') {
      return { from: weekStart, to: ymd(addDays(parseYmd(weekStart), 6)) };
    }
    if (periodType === 'four') {
      return { from: cycleStart, to: ymd(addDays(parseYmd(cycleStart), 27)) };
    }
    if (periodType === 'custom') {
      if (!isYmd(customFrom) || !isYmd(customTo) || customFrom > customTo) return null;
      return { from: customFrom, to: customTo };
    }
    return null; // monthly uses the API's month shorthand
  }, [periodType, weekStart, cycleStart, customFrom, customTo]);

  // What is stopping a custom range from being run, in the manager's words.
  const customHint = periodType !== 'custom' || range
    ? null
    : (!isYmd(customFrom) || !isYmd(customTo)
      ? 'Pick a start and an end date to run pay for that period.'
      : 'The start date is after the end date — swap them to run this period.');

  const fetchRun = useCallback(async () => {
    if (!allowed || tab !== 'run') return;
    // An incomplete custom range: leave the last result alone and wait.
    if (periodType !== 'month' && !range) {
      setRunLoading(false);
      return;
    }
    try {
      setRunLoading(true);
      const res = await payrollService.getPayroll(
        periodType === 'month' ? { month } : range,
      );
      if (res?.success) setRun(res.data);
      else toast.error(res?.message || 'Could not load the pay run');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load the pay run');
    } finally {
      setRunLoading(false);
    }
  }, [allowed, tab, month, periodType, range]);

  useEffect(() => { fetchRun(); }, [fetchRun]);

  const fetchRates = useCallback(async () => {
    if (!allowed || tab !== 'rates') return;
    try {
      setRatesLoading(true);
      const res = await payrollService.getRates();
      if (res?.success) setRates(res.data);
      else toast.error(res?.message || 'Could not load hourly rates');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load hourly rates');
    } finally {
      setRatesLoading(false);
    }
  }, [allowed, tab]);

  /**
   * A starter CSV, pre-filled with this organisation's staff and whatever rate
   * they already have — so the file comes back with the right codes in it
   * rather than a manager having to look each one up.
   */
  const downloadRateTemplate = () => {
    const rows = [['employee_code', 'employee_name', 'hourly_rate']];
    (rates?.employees || []).forEach((r) => {
      // Quote the name: a "Smith, John" would otherwise shift the rate column.
      rows.push([r.employee_code || '', `"${String(r.employee_name || '').replace(/"/g, '""')}"`, r.hourly_rate ?? '']);
    });
    const csv = rows.map((row) => row.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'hourly-rates.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  /** Picked a file: show what it would do, write nothing yet. */
  const handleRateFile = async (event) => {
    const file = event.target.files?.[0];
    // Clear immediately so choosing the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      const res = await payrollService.importRates(file, { preview: true });
      if (res?.success) {
        setRatePreview({ fileName: file.name, file, changes: res.data.changes });
      } else {
        toast.error(res?.message || 'Could not read that file');
      }
    } catch (err) {
      const data = err?.response?.data;
      // The API returns the offending line numbers; a bare "invalid file" would
      // leave the manager guessing which row to fix.
      if (data?.errors?.length) {
        toast.error(`${data.message}\n\n${data.errors.slice(0, 5).join('\n')}`, { duration: 10000 });
      } else {
        toast.error(data?.message || 'Could not read that file');
      }
    } finally {
      setImporting(false);
    }
  };

  /** Confirmed the preview: apply it. */
  const applyRateImport = async () => {
    if (!ratePreview?.file) return;
    try {
      setImporting(true);
      const res = await payrollService.importRates(ratePreview.file);
      if (res?.success) {
        toast.success(res.message, { duration: 8000 });
        setRatePreview(null);
        fetchRates();
      } else {
        toast.error(res?.message || 'Could not apply those rates');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not apply those rates');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const submitRate = async (e) => {
    e.preventDefault();
    const target = editRate;
    // An empty box means "clear it" — a real action for somebody paid outside
    // the system — and is passed through as null rather than coerced to 0.
    const clearing = String(target.rate).trim() === '';
    const value = clearing ? null : Number(target.rate);
    if (!clearing && (!Number.isFinite(value) || value < 0)) {
      return toast.error('Enter the hourly rate as a positive amount, or leave it empty to clear it.');
    }

    try {
      setSavingRate(true);
      const res = await payrollService.updateRate(target.employee_id, value);
      if (res?.success) {
        // Shown verbatim: the API's message is the one that says the change
        // applies to earlier months too, which is the thing a manager needs
        // to hear.
        toast.success(res.message || 'Hourly rate updated');
        setEditRate(null);
        fetchRates();
        // Every figure in the loaded pay run was calculated from the old rate.
        setRun(null);
      } else {
        toast.error(res?.message || 'Could not update the rate');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update the rate');
    } finally {
      setSavingRate(false);
    }
    return undefined;
  };

  /**
   * CSV of the individual payable lines — one row per shift or visit, which is
   * what a payroll system wants to import.
   *
   * Deliberately the LINES and not the per-person summary: a clerk reconciling a
   * query needs to see which day the hours came from, and a total can always be
   * re-summed from lines while lines can never be recovered from a total.
   */
  const download = () => {
    const rows = run?.rows || [];
    if (!rows.length) return;
    // The file is named for the period on screen, so the two can never disagree.

    // Leave carries its own columns rather than being mixed into payable
    // hours: it is not worked time, and a file that added the two together
    // would overstate every absent person's month.
    const head = [
      'Employee', 'Staff ID', 'Job title', 'Work date', 'Source', 'Pay code',
      'Unpaid break (min)', 'Payable hours', 'Hourly rate', 'Pay',
      'Leave type', 'Leave days', 'Leave hours',
    ];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = rows.map((r) => [
      r.employee, r.employee_code, r.job_title, r.work_date, r.source, r.pay_code,
      r.unpaid_break, r.payable_hours,
      r.hourly_rate == null ? '' : Number(r.hourly_rate).toFixed(2),
      r.pay == null ? '' : Number(r.pay).toFixed(2),
      '', '', '',
    ].map(esc).join(','));

    // One row per absence, clipped to the period by the API, with the pay
    // columns left empty — no pay is calculated for leave here.
    const leaveBody = (run?.leave_lines || []).map((l) => [
      l.employee, l.employee_code, l.job_title, l.start_date, l.leave_label,
      LEAVE_CODES[l.leave_type] || 'LVE', '', '', '', '',
      l.leave_label, l.days, l.hours || '',
    ].map(esc).join(','));

    const csv = [head.map(esc).join(','), ...body, ...leaveBody].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `payroll-${run.from}-to-${run.to}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    const leaveCount = (run?.leave_lines || []).length;
    toast.success(
      `Exported ${rows.length} line${rows.length === 1 ? '' : 's'}`
      + (leaveCount ? ` and ${leaveCount} leave entr${leaveCount === 1 ? 'y' : 'ies'}` : ''),
    );
  };

  /**
   * One row per person: hours, pay, and leave split by type.
   *
   * The lines file above is for importing into a payroll system; this is the
   * file a manager actually reads — "how many days did each person have off,
   * and of what kind" is a question the per-line file can only answer with a
   * pivot table.
   */
  const downloadSummary = () => {
    const people = run?.employees || [];
    if (!people.length) return;

    const head = [
      'Employee', 'Staff ID', 'Job title', 'Hourly rate', 'Payable hours', 'Pay',
      'Annual leave (days)', 'Sickness (days)', 'Other leave (days)', 'Total leave (days)',
      'Annual leave (hours)', 'Sickness (hours)', 'Other leave (hours)',
    ];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = people.map((e) => [
      e.employee, e.employee_code, e.job_title,
      e.hourly_rate == null ? '' : Number(e.hourly_rate).toFixed(2),
      e.total_hours,
      // Blank, not 0.00, where nobody has set a rate — the same distinction the
      // table makes between "earned nothing" and "nobody has said what they earn".
      e.needs_rate ? '' : Number(e.total_pay || 0).toFixed(2),
      e.leave?.annual_days || 0, e.leave?.sick_days || 0, e.leave?.other_days || 0,
      e.leave?.total_days || 0,
      e.leave?.annual_hours || 0, e.leave?.sick_hours || 0, e.leave?.other_hours || 0,
    ].map(esc).join(','));

    const csv = [head.map(esc).join(','), ...body].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `payroll-summary-${run.from}-to-${run.to}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${people.length} staff member${people.length === 1 ? '' : 's'}`);
  };

  if (!allowed) {
    return (
      <div className="w-full space-y-6 pb-8">
        <PageHeader title="Payroll" />
        <div className={`${CARD} p-8 text-center`}>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400">
            Only managers can view staff pay.
          </p>
        </div>
      </div>
    );
  }

  const summary = run?.summary || {};

  const tile = (label, value, tone = 'text-[#0B1220] dark:text-gray-100') => (
    <div className={`${CARD} p-4`}>
      <div className={`text-[22px] font-extrabold ${tone}`}>{value}</div>
      <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{label}</div>
    </div>
  );

  const employeeColumns = [
    {
      key: 'employee',
      header: 'Staff member',
      render: (r) => <Person name={r.employee} subtitle={r.job_title || r.employee_code} />,
    },
    {
      key: 'rate',
      header: 'Hourly rate',
      render: (r) => (
        r.hourly_rate == null ? (
          <span className="text-[12.5px] text-amber-600 dark:text-amber-400">Not set</span>
        ) : (
          <span className="text-[13px] text-gray-700 dark:text-gray-300">{money(r.hourly_rate)}</span>
        )
      ),
    },
    {
      key: 'hours',
      header: 'Payable hours',
      render: (r) => (
        <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
          {hoursLabel(r.total_hours)}
        </span>
      ),
    },
    {
      key: 'leave',
      header: 'Leave in period',
      render: (r) => {
        const total = Number(r.leave?.total_days || 0);
        if (!total) return <span className="text-[12.5px] text-[#94A3B8]">—</span>;
        // The split matters more than the total, so it is shown, not hidden
        // behind a tooltip: "5 days off" reads very differently once you know
        // four of them were sickness.
        const parts = LEAVE_KINDS
          .filter((k) => Number(r.leave?.[`${k.key}_days`] || 0) > 0)
          .map((k) => `${k.short} ${hoursLabel(r.leave[`${k.key}_days`])}`);
        return (
          <div>
            <span className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">{daysLabel(total)}</span>
            <div className="text-[11.5px] text-[#64748B] dark:text-gray-400">{parts.join(' · ')}</div>
          </div>
        );
      },
    },
    {
      key: 'pay',
      header: 'Pay',
      render: (r) => (
        <div>
          <span className={`text-[13.5px] font-bold ${r.needs_rate
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-[#0B1220] dark:text-gray-100'}`}
          >
            {/* Hours with no rate are NOT shown as 0.00 — that reads as "worked
                for nothing" rather than "nobody has set their rate". */}
            {r.needs_rate ? 'Needs a rate' : money(r.total_pay)}
          </span>
        </div>
      ),
    },
    {
      key: 'entries',
      header: 'Lines',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-[13px] text-gray-600 dark:text-gray-400">{r.entries || 0}</span>
          <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
        </div>
      ),
    },
  ];

  const rateColumns = [
    {
      key: 'employee',
      header: 'Staff member',
      render: (r) => (
        <Person
          name={r.employee_name}
          subtitle={r.job_title || r.employee_code}
        />
      ),
    },
    {
      key: 'employment',
      header: 'Employment',
      render: (r) => (
        <span className="text-[12.5px] text-gray-600 dark:text-gray-400">
          {r.employment_type || '—'}
        </span>
      ),
    },
    {
      key: 'rate',
      header: 'Hourly rate',
      render: (r) => (
        <div>
          {r.hourly_rate == null ? (
            <span className="text-[12.5px] font-semibold text-amber-600 dark:text-amber-400">
              Not set
            </span>
          ) : (
            <span className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">
              {money(r.hourly_rate)}
            </span>
          )}
          {/* The old free-text value, as context — it is what somebody typed
              before there was a numeric field, and it explains the suggestion. */}
          {r.legacy_pay_rate && (
            <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 max-w-[180px] truncate" title={r.legacy_pay_rate}>
              note: {r.legacy_pay_rate}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: '',
      render: (r) => (
        !r.is_active
          ? <StatusPill tone="mute">Inactive</StatusPill>
          : (r.suggested_rate != null
            ? <StatusPill tone="warn">Suggestion available</StatusPill>
            : null)
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditRate({
            employee_id: r.employee_id,
            employee_name: r.employee_name,
            rate: r.hourly_rate == null ? '' : String(r.hourly_rate),
            suggested: r.suggested_rate,
            legacy: r.legacy_pay_rate,
          })}
        >
          {r.hourly_rate == null ? 'Set rate' : 'Edit'}
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        title="Payroll"
        subtitle="Hourly rates and worked-hours export"
        actions={tab === 'run' ? (
          // Disabled while a custom range is half-typed: the figures on screen
          // are still the last period's, and exporting them under a new pair of
          // dates would misreport what was paid.
          <div className="flex items-center gap-2">
            {/* Two files, two jobs: the per-person summary is what a manager
                reads, the per-line file is what a payroll system imports. */}
            <Button
              variant="secondary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={downloadSummary}
              disabled={!(run?.employees || []).length || !!customHint}
            >
              Staff summary
            </Button>
            <Button
              variant="primary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={download}
              disabled={!(run?.rows || []).length || !!customHint}
            >
              Export CSV
            </Button>
          </div>
        ) : null}
      />

      {tab === 'run' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {tile('Payable hours', hoursLabel(summary.total_hours))}
          {tile('Total pay', money(summary.total_pay), 'text-emerald-600')}
          {tile('Staff paid', summary.paid_staff ?? 0, 'text-blue-600')}
          {tile(
            'Not included',
            summary.not_included ?? 0,
            summary.not_included > 0 ? 'text-amber-500' : undefined,
          )}
          {/* Leave is NOT part of hours or pay — this system doesn't calculate
              leave pay. It is here because a short month is usually explained
              by it, and because the split is what payroll needs. */}
          <div className={`${CARD} p-4`}>
            <div className="text-[22px] font-extrabold text-[#0B1220] dark:text-gray-100">
              {hoursLabel(summary.total_leave_days || 0)}
            </div>
            <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">
              Leave days
            </div>
            <div className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-1">
              Annual {hoursLabel(summary.annual_leave_days || 0)}
              {' · '}Sick {hoursLabel(summary.sick_leave_days || 0)}
              {' · '}Other {hoursLabel(summary.other_leave_days || 0)}
            </div>
          </div>
        </div>
      )}

      <div className={`${CARD} overflow-hidden`}>
        <div className="flex items-center gap-1 p-3 border-b border-[#E2E8F0] dark:border-gray-800 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition ${tab === t.key
                ? 'bg-blue-600 text-white'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f]'
                }`}
            >
              {t.label}
              {t.key === 'rates' && rates?.without_rate > 0 && (
                <span className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.key ? 'bg-white/25' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                  {rates.without_rate}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'run' ? (
          <>
            <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[#E2E8F0] dark:border-gray-800">
              <p className="flex-1 min-w-[260px] text-[13px] text-[#64748B] dark:text-gray-400">
                Approved care-home hours and completed domiciliary visits for the
                period below. Hours still awaiting approval, and visits without a
                clock-out, are listed as not included rather than added in.
              </p>
              {/* Which cycle this organisation runs pay on. */}
              <div className="flex items-center gap-1 p-1 rounded-full bg-[#F1F5F9] dark:bg-[#31373f]">
                {PERIOD_TYPES.map((pt) => (
                  <button
                    key={pt.key}
                    type="button"
                    onClick={() => setPeriodType(pt.key)}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition ${periodType === pt.key
                      ? 'bg-white dark:bg-[#262b33] text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                  >
                    {pt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* The period itself. Each cycle gets the control that suits it:
                whole months, whole cycles, whole weeks — or two free dates. */}
            <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 border-b border-[#E2E8F0] dark:border-gray-800">
              <CalendarRange className="w-4 h-4 text-[#94A3B8] shrink-0" />

              {periodType === 'month' && (
                <div className="w-[190px]">
                  <FormField
                    name="month"
                    type="select"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    options={months}
                  />
                </div>
              )}

              {periodType === 'four' && (
                <div className="w-[260px]">
                  <FormField
                    name="cycle"
                    type="select"
                    value={cycleStart}
                    onChange={(e) => setCycleStart(e.target.value)}
                    options={cycles}
                  />
                </div>
              )}

              {periodType === 'week' && (
                <div className="w-[260px]">
                  <FormField
                    name="week"
                    type="select"
                    value={weekStart}
                    onChange={(e) => setWeekStart(e.target.value)}
                    options={weeks}
                  />
                </div>
              )}

              {periodType === 'custom' && (
                <>
                  <div className="w-[180px]">
                    <FormField
                      label="From"
                      name="custom_from"
                      type="date"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      max={customTo || undefined}
                    />
                  </div>
                  <div className="w-[180px]">
                    <FormField
                      label="To"
                      name="custom_to"
                      type="date"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      min={customFrom || undefined}
                    />
                  </div>
                </>
              )}

              {customHint ? (
                <p className="text-[12.5px] text-amber-600 dark:text-amber-400">{customHint}</p>
              ) : (
                run && (
                  <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                    Showing <span className="font-semibold text-[#0B1220] dark:text-gray-200">{rangeLabel(run.from, run.to)}</span>
                    {' '}— the CSV exports exactly this period.
                  </p>
                )
              )}
            </div>

            {/* The two things that make a total worth questioning, said before
                the total is read rather than after it is exported. */}
            {summary.needs_rate > 0 && (
              <div className="flex items-start gap-2.5 px-4 py-3 border-b border-[#E2E8F0] dark:border-gray-800 bg-amber-50 dark:bg-amber-900/10">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {summary.needs_rate} staff member{summary.needs_rate === 1 ? '' : 's'} worked
                    but has no hourly rate set.
                  </span>
                  {' '}Their hours are counted, but there is no pay figure — set a rate on the
                  Hourly rates tab and the pay appears.
                </p>
              </div>
            )}

            <DataTable
              bare
              columns={employeeColumns}
              data={run?.employees || []}
              loading={runLoading}
              rowKey={(r) => r.employee_id}
              onRowClick={(r) => (r.entries > 0 ? setDetail(r) : null)}
              emptyIcon={Coins}
              emptyTitle="Nothing to pay in this period"
              emptyText="Approved care-home hours and completed visits appear here once there are some."
            />

            {(run?.exceptions || []).length > 0 && (
              <div className="p-4 border-t border-[#E2E8F0] dark:border-gray-800">
                <p className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-3">
                  Not included in this pay run ({run.exceptions.length})
                </p>
                <div className="space-y-2">
                  {run.exceptions.map((x, i) => (
                    <div
                      key={`${x.employee_id}-${x.work_date}-${x.source}-${i}`}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl border border-[#E2E8F0] dark:border-gray-800 p-3"
                    >
                      <span className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
                        {x.employee}
                      </span>
                      <span className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                        {x.work_date} · {x.source}
                        {x.hours > 0 ? ` · ${hoursLabel(x.hours)}h` : ''}
                      </span>
                      <span className="text-[12.5px] text-amber-700 dark:text-amber-400 basis-full">
                        {x.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start gap-2 px-4 py-3 border-b border-[#E2E8F0] dark:border-gray-800">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB] dark:text-blue-400" />
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                {/* The single most important thing to know about this screen. */}
                A rate is stored in one place and read live, so changing it{' '}
                <strong className="text-[#0B1220] dark:text-gray-200">also changes what earlier
                months export as</strong>. Download the CSV for a period you have paid — that file
                is the record. Rates can also be set on a staff member's Employment details.
              </p>
            </div>

            {/* Bulk rates. Typing a rate for every carer is work a spreadsheet
                already did once, so accept the spreadsheet. */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#E2E8F0] dark:border-gray-800">
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                Setting a lot of rates? Upload a CSV with an{' '}
                <code className="text-[12px] font-mono text-[#0B1220] dark:text-gray-200">employee_code</code> and an{' '}
                <code className="text-[12px] font-mono text-[#0B1220] dark:text-gray-200">hourly_rate</code> column.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={downloadRateTemplate}
                  className="text-[12.5px] font-semibold text-[#2563EB] hover:underline"
                >
                  Download template
                </button>
                <input
                  ref={rateFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleRateFile}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  loading={importing}
                  onClick={() => rateFileRef.current?.click()}
                >
                  Upload rates
                </Button>
              </div>
            </div>

            {/* What the file would do, before it does it. */}
            {ratePreview && (
              <div className="px-4 py-3 border-b border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c]">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-200">
                    {ratePreview.changes.length} rate{ratePreview.changes.length === 1 ? '' : 's'} ready to apply
                    <span className="font-normal text-[#64748B] dark:text-gray-400"> from {ratePreview.fileName}</span>
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setRatePreview(null)}>Cancel</Button>
                    <Button variant="primary" size="sm" loading={importing} onClick={applyRateImport}>Apply</Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto text-[12.5px] text-[#475569] dark:text-gray-400 space-y-0.5">
                  {ratePreview.changes.map((change) => (
                    <div key={change.employeeId} className="flex justify-between gap-4">
                      <span className="truncate">{change.name}</span>
                      <span className="tabular-nums shrink-0">
                        {change.was == null ? '—' : Number(change.was).toFixed(2)} → {change.rate == null ? 'cleared' : Number(change.rate).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DataTable
              bare
              columns={rateColumns}
              data={rates?.employees || []}
              loading={ratesLoading}
              rowKey={(r) => r.employee_id}
              emptyIcon={Coins}
              emptyTitle="No staff to show"
              emptyText="Hourly rates appear here once your organisation has staff."
            />
          </>
        )}
      </div>

      <Modal
        isOpen={!!editRate}
        onClose={() => setEditRate(null)}
        title={`${editRate?.employee_name || ''} — hourly rate`}
        maxWidth="max-w-[440px]"
      >
        {editRate && (
          <form onSubmit={submitRate} className="space-y-4">
            <FormField
              label="Hourly rate"
              name="hourly_rate"
              type="number"
              min="0"
              step="0.01"
              value={editRate.rate}
              onChange={(e) => setEditRate((s) => ({ ...s, rate: e.target.value }))}
              placeholder="e.g. 12.50"
            />
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 -mt-2">
              Leave empty to clear the rate — their hours are then reported without a pay figure.
            </p>

            {/* Offered, never applied. The old field is free text, and a wrong
                guess here becomes somebody's wages. */}
            {editRate.suggested != null && (
              <div className="flex items-start gap-2 rounded-xl border border-[#DBE7FF] dark:border-blue-900/40 bg-[#F5F9FF] dark:bg-blue-900/10 px-3.5 py-2.5">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2563EB] dark:text-blue-400" />
                <div className="text-[12px] text-[#64748B] dark:text-gray-400">
                  Their employment record says
                  {' '}<span className="font-semibold text-[#0B1220] dark:text-gray-200">
                    “{editRate.legacy}”
                  </span>.
                  <button
                    type="button"
                    onClick={() => setEditRate((s) => ({ ...s, rate: String(s.suggested) }))}
                    className="ml-1 font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Use {money(editRate.suggested)}
                  </button>
                  {' '}— check it first; that field is free text and was never used for pay.
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F1F5F9] dark:border-gray-800">
              <Button type="button" variant="secondary" onClick={() => setEditRate(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={savingRate}>
                {savingRate ? 'Saving…' : 'Save rate'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={`${detail?.employee || ''} — ${run?.from} to ${run?.to}`}
        maxWidth="max-w-[640px]"
      >
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-px bg-[#E2E8F0] dark:bg-gray-800 rounded-lg overflow-hidden border border-[#E2E8F0] dark:border-gray-800">
              {[
                ['Payable hours', hoursLabel(detail.total_hours)],
                ['Hourly rate', detail.hourly_rate == null ? 'Not set' : money(detail.hourly_rate)],
                ['Pay', detail.needs_rate ? 'Needs a rate' : money(detail.total_pay)],
              ].map(([label, value]) => (
                <div key={label} className="bg-white dark:bg-[#262b33] p-3">
                  <div className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{value}</div>
                  <div className="text-[11.5px] text-[#64748B] dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2 max-h-[46vh] overflow-y-auto">
              {(run?.rows || [])
                .filter((r) => Number(r.employee_id) === Number(detail.employee_id))
                .map((r, i) => (
                  <div
                    key={`${r.work_date}-${r.source}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#E2E8F0] dark:border-gray-800 p-3"
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
                        {r.work_date}
                      </div>
                      <div className="text-[12px] text-[#64748B] dark:text-gray-400">
                        {r.source}
                        {r.unpaid_break > 0 ? ` · ${r.unpaid_break} min unpaid break` : ''}
                        {r.hourly_rate == null ? ' · no rate set' : ''}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-[13px] font-bold text-gray-700 dark:text-gray-200">
                        {hoursLabel(r.payable_hours)}h
                      </div>
                      <div className="text-[12px] text-[#64748B] dark:text-gray-400">
                        {r.pay == null ? '—' : money(r.pay)}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payroll;
